import { WorkSheetProductModelType } from '@teinor/erp/types/company/order/sell/workSheet/workSheetProduct'
import { permissionCheck } from '../../../../../../../../../baseComponents/PermissionChecker/function'
import { InputTableLayoutStepProps } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { ColumnOrderTableSchema } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import WorkSheetLayout from '../../../../../../../../../specificComponents/inputTableLayouts/order/WorkSheetLayout'
import { ProductExtendedTyInputTableListing } from '../../../../../../../../../specificComponents/Product/types'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { getAllLabels, getAllWorkSheetGroupStatuses, getEmployees, getUserPermissions } from '../../../../../../../../../utils/reducers/getters'
import WorkSheetHeader from '../WorkSheetHeader'
import WorkSheetProductStatusSelector from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/Inputs/WorkSheetProductStatusSelector'
import { addDictionaryExtraLine, dictionaryComplexFromJsonArr } from '../../../../../../../../../utils/globals/dictionary'
import { sortEmployeesByUser } from '../../../../../../../../../utils/globals/employees'
import { useMemo } from 'react'
import { ChangeQuantityButton, PrintWorkSheetTagsButton, WorkSheetProductCancelStatus, WorkSheetProductDeleteStatus, WorkSheetProductSplitStatus } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/Inputs/WorkSheetProductStatusInput'
import { CustomRenderString } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import useEditDeleteModal from '../../../../../../../../../utils/hooks/useEditDeleteModal'
import ModalChangeQuantityWorkSheetProduct from '../../../../../../../../../specificComponents/inputTableLayouts/order/WorkSheetLayout/ModalsWorkSheetLayout/ModalChangeQuantityWorkSheetProduct'
import ModalPrintWorkSheetTags from '../../../../../../../../../specificComponents/inputTableLayouts/order/WorkSheetLayout/ModalsWorkSheetLayout/ModalPrintWorkSheetTags'
import SingleWorkSheetHeaderDecider from './SingleWorkSheetInnerHeader'

type SingleWorkSheetInnerProps = InputTableLayoutStepProps & {
    handleChangeView: () => void
}

interface WorkSheetProductModelTypeExtended extends WorkSheetProductModelType {
    selected: boolean
}

const SingleWorkSheetInner: React.FC<SingleWorkSheetInnerProps> = ({ tableConfiguration, setTableConfiguration, userPermissions, inputTableURLs, completeData, setCompleteData, getCompleteData, handleChangeView }) => {
    const { t, tVars } = useTwinTranslation()
    const { openModal, setOpenModal, memorizedEmployees } = useSingleWorkSheetInnerLogic()
    if (!completeData) {
        return null
    }
    const workSheetProductStatuses = getAllWorkSheetGroupStatuses()
    const myWorkSheetStatus = workSheetProductStatuses?.dictWorkSheetGroupStatus?.[completeData?.header?.WorkSheetGroupStatusId]?.WorkSheetProductStatusesDict || {}
    const labels = getAllLabels()
    const extraColumnLabel: any[] = []
    if (labels && Object.keys(labels).length) {
        extraColumnLabel.push({
            id: 'printTag',
            dataKey: 'id',
            label: '',
            simpleLabel: t('printTag', 'Imprimir etiqueta'),
            customRender: (parameterValue: any, allRowData: any) => <PrintWorkSheetTagsButton id={parameterValue} allRowData={allRowData} setOpenModal={setOpenModal} />,
            width: 33,
        },)
    }
    const columns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & WorkSheetProductModelTypeExtended> = [
        {
            id: 'selected',
            dataKey: 'selected',
            label: '',
            simpleLabel: t('selectLine', 'Seleccionar línea'),
            width: 50,
            requiredColumn: true,
            component: 'ITCheckbox',
        },
        {
            id: 'nLine',
            dataKey: 'nLine',
            label: t('line', 'Línea'),
            width: 70,
        },
        {
            id: 'url',
            dataKey: 'url',
            label: t('image', 'Imagen'),
            component: 'ITDisplayImage',
            width: 70
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            simpleLabel: t('codeBar', 'Código de barras'),
            component: 'OrderSearchProductInput',
            field: 'code_bar',
            sortable: true,
            hideColumnByDefault: true,
            minWidth: 115
        },
        {
            id: 'referenceAndName',
            dataKey: 'name',
            label: t('referenceName', 'Referencia y nombre'),
            component: 'OrderSearchProductInputReferenceAndName',
            field: 'referenceAndName',
            minWidth: 180,
            className: 'quote_order_reference_name'
        },
        {
            id: 'descriptionDocuments',
            dataKey: 'descriptionDocuments',
            label: t('description', 'Descripción'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'shortDescriptionDocuments',
            dataKey: 'shortDescriptionDocuments',
            label: t('shortDescriptionS', 'Desc. corta'),
            simpleLabel: t('shortDescription', 'Descripción corta'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'WorkSheetProductStatusId',
            dataKey: 'WorkSheetProductStatusId',
            label: t('status', 'Estado'),
            customRender: (parameterValue: any, allRowData: any) => <WorkSheetProductStatusSelector workSheetProductStatuses={myWorkSheetStatus} value={parameterValue} updateDataURL={inputTableURLs.updateData} {...{ setCompleteData, allRowData, getCompleteData }} />,
            width: 130,
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            component: 'ITEditableText',
            typeOnChange: 'onChangeFetch',
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            component: 'ITInputQuantity',
            width: 100,
            sortable: true,
            readOnlyFnc: () => true,
        },
        {
            id: 'EmployeeId',
            dataKey: 'AssignedEmployeeId',
            label: t('employee', 'Empleado'),
            component: 'ITInputSelectWithSeachBar',
            minWidth: 110,
            items: addDictionaryExtraLine(memorizedEmployees || {}),
            fieldName: 'fullname_short',
            sortFunction: sortEmployeesByUser,
        },
        {
            id: 'splitRow',
            dataKey: 'id',
            label: '',
            simpleLabel: t('dividedProductStatus', 'Dividir el estado del producto'),
            customRender: (_parameterValue: any, allRowData: any) => <WorkSheetProductSplitStatus {...{ getCompleteData }} allRowData={{
                product: allRowData, WorkSheetProductStatusId: undefined, order: undefined
            }} workSheetGroupStatusId={completeData?.header?.WorkSheetGroupStatusId} />,
            width: 33,
        },
        ...extraColumnLabel,
        {
            id: 'deleteRow',
            dataKey: 'id',
            label: '',
            simpleLabel: t('deleteLine', 'Eliminar línea'),
            customRender: (_parameterValue: any, allRowData: any) => <WorkSheetProductDeleteStatus deleteURL={'/api/app/order/sell/workSheet/workSheetProduct/deleteWorkSheetProduct'} {...{ getCompleteData, allRowData }} />,
            width: 33,
        },
        {
            id: 'cancelRow',
            dataKey: 'id',
            label: '',
            simpleLabel: t('cancelLine', 'Cancelar línea'),
            customRender: (_parameterValue: any, allRowData: any) => <WorkSheetProductCancelStatus WorkSheetGroupStatusId={completeData?.header?.WorkSheetGroupStatusId} cancelURL={'/api/app/order/sell/workSheet/workSheetProduct/cancelStatusToWorkSheetProduct'} {...{ getCompleteData, allRowData }} />,
            width: 33,
        },
        {
            id: 'dragLine',
            dataKey: 'nLine',
            label: '',
            simpleLabel: t('moveLine', 'Mover línea'),
            width: 33,
            component: 'ITDragHandle',
            requiredColumn: true,
        },
        {
            id: 'unique_number',
            dataKey: 'unique_number',
            label: t('uniqueNumberShort', 'Nº único'),
            simpleLabel: t('uniqueNumber', 'Número único'),
            toolTip: t('uniqueNumber', 'Número único'),
            component: 'OrderUniqueNumberProductReadOnly',
            hideColumnByDefault: true,
        },
        {
            id: 'changeQuantity',
            dataKey: 'id',
            label: '',
            simpleLabel: t('changeQuantity', 'Cambiar cantidad'),
            customRender: (parameterValue: any, allRowData: any) => <ChangeQuantityButton id={parameterValue} allRowData={allRowData} setOpenModal={setOpenModal} className='w-16'/>,
            width: 33,
        }
    ]
    const options = { fullAssignedNumber: completeData?.header?.custom_name || completeData?.header?.full_assigned_number, CustomerName: completeData?.header?.Customer?.fiscal_name || completeData?.header?.Customer?.name }
    const title = tVars('singleWorkSheetWithId', 'Hoja de trabajo {{fullAssignedNumber}}', options)
    return (
        <WorkSheetLayout inputTableURLs={inputTableURLs} name='workSheetTable' translations={{ headerTitle: title }} className='workSheetTable' buttonSave={permissionCheck(getUserPermissions('sell.workSheetProductStatus'), 'update')
            ?
            <SingleWorkSheetHeaderDecider completeData={completeData} onSubmit={getCompleteData} getCompleteData={getCompleteData} handleChangeView={handleChangeView} />
            : undefined} InputTableHeaderChildren={WorkSheetHeader} {...{ columns, completeData, setCompleteData, getCompleteData, setTableConfiguration, tableConfiguration, userPermissions }}>
            {openModal?.type === 'edit' && openModal.allRowData?.subType === 'changeQuantity' ? <ModalChangeQuantityWorkSheetProduct allRowData={openModal?.allRowData} setOpenModal={setOpenModal} getData={getCompleteData} /> : null}
            {openModal?.type === 'edit' && openModal.allRowData?.subType === 'printTags' ? <ModalPrintWorkSheetTags setOpened={() => setOpenModal(null)} onSubmit={() => { }} allRowData={openModal?.allRowData} /> : null}
        </WorkSheetLayout>
    )
}

const useSingleWorkSheetInnerLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()

    const memorizedEmployees = useMemo(() => {
        return dictionaryComplexFromJsonArr(getEmployees() || [])
    }, [])
    return { openModal, setOpenModal, memorizedEmployees }
}


export default SingleWorkSheetInner

